import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Paper,
  Grid,
  Chip,
  Tooltip,
  Alert,
  Divider
} from '@mui/material';
import {
  Info as InfoIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import RetryStrategySelector from './RetryStrategySelector';
import { api } from '../../services/authService';

const RetryConfigurationPanel = ({
  retryStrategy,
  maxRetries,
  onRetryStrategyChange,
  onMaxRetriesChange,
  showAdvanced = false,
  showInheritance = false,
  inheritedFrom = null,
  label = "Retry Configuration",
  description = "Configure retry behavior for failed messages",
  disabled = false
}) => {
  const [strategyData, setStrategyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch strategy data when retryStrategy changes
  useEffect(() => {
    if (retryStrategy && retryStrategy !== 'default') {
      fetchStrategyData(retryStrategy);
    } else {
      setStrategyData(null);
    }
  }, [retryStrategy]);

  const fetchStrategyData = async (strategyId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/acs/retry-strategies/${strategyId}/`);
      setStrategyData(response.data);
    } catch (err) {
      setError('Failed to load strategy details');
      console.error('Error fetching strategy data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getEffectiveMaxRetries = () => {
    // Priority: override > strategy > default
    if (maxRetries !== null && maxRetries !== undefined) {
      return maxRetries;
    }
    if (strategyData) {
      return strategyData.max_attempts;
    }
    return 3; // Default fallback
  };

  const getEffectiveDelay = (attemptNumber) => {
    if (!strategyData) return 30; // Default fallback

    let delay = strategyData.base_delay_minutes;
    for (let i = 1; i < attemptNumber; i++) {
      delay *= strategyData.backoff_factor;
    }
    return Math.min(delay, strategyData.max_delay_minutes);
  };

  const renderInheritanceIndicator = () => {
    if (!showInheritance || !inheritedFrom) return null;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <InfoIcon color="info" fontSize="small" />
        <Typography variant="body2" color="text.secondary">
          Inheriting from: <strong>{inheritedFrom}</strong>
        </Typography>
      </Box>
    );
  };

  const renderRetryPreview = () => {
    const effectiveMaxRetries = getEffectiveMaxRetries();
    const delays = [];

    for (let attempt = 1; attempt <= effectiveMaxRetries; attempt++) {
      delays.push(getEffectiveDelay(attempt));
    }

    return (
      <Paper sx={{ p: 2, mt: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" gutterBottom>
          Effective Retry Behavior
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          {delays.map((delay, index) => (
            <Box
              key={index}
              sx={{
                p: 1,
                bgcolor: 'background.default',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                minWidth: 80,
                textAlign: 'center'
              }}
            >
              <Typography variant="caption" display="block" color="text.secondary">
                Attempt {index + 1}
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="text.primary">
                {delay} min
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary">
          Total time: {delays.reduce((sum, delay) => sum + delay, 0)} minutes
        </Typography>
      </Paper>
    );
  };

  const renderStrategyInfo = () => {
    if (!strategyData) return null;

    return (
      <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" gutterBottom>
          Strategy Details: {strategyData.name}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Base Delay
            </Typography>
            <Typography variant="body2" color="text.primary">
              {strategyData.base_delay_minutes} minutes
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Backoff Factor
            </Typography>
            <Typography variant="body2" color="text.primary">
              {strategyData.backoff_factor}x
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Max Delay
            </Typography>
            <Typography variant="body2" color="text.primary">
              {strategyData.max_delay_minutes} minutes
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Max Attempts
            </Typography>
            <Typography variant="body2" color="text.primary">
              {strategyData.max_attempts}
            </Typography>
          </Grid>
        </Grid>
        {strategyData.description && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {strategyData.description}
          </Typography>
        )}
      </Box>
    );
  };

  const renderAdvancedSettings = () => {
    if (!showAdvanced || !strategyData) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>
          Advanced Settings
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={strategyData.retry_on_failure}
                  disabled
                />
              }
              label="Retry on Failures"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={strategyData.retry_on_timeout}
                  disabled
                />
              }
              label="Retry on Timeouts"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={strategyData.retry_on_error}
                  disabled
                />
              }
              label="Retry on Errors"
            />
          </Grid>
        </Grid>
        {strategyData.notify_on_max_retries && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Notifications: {strategyData.notify_recipients || 'No recipients configured'}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {label}
      </Typography>
      
      {description && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {description}
        </Typography>
      )}

      {renderInheritanceIndicator()}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <RetryStrategySelector
            value={retryStrategy}
            onChange={onRetryStrategyChange}
            disabled={disabled}
            label="Retry Strategy"
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Max Retries Override"
            type="number"
            value={maxRetries || ''}
            onChange={(e) => onMaxRetriesChange(e.target.value ? parseInt(e.target.value) : null)}
            inputProps={{ min: 1, max: 10 }}
            helperText="Leave empty to use strategy default"
            disabled={disabled}
          />
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {renderStrategyInfo()}
      {renderRetryPreview()}
      {renderAdvancedSettings()}
    </Box>
  );
};

export default RetryConfigurationPanel; 